/**
 * Member Dashboard Overview Page
 * 
 * Shows summary cards and quick actions for the member
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/utils/auth';
import apiClient from '@/utils/api';
import { formatCurrency } from '@/utils/format';
import { toast } from 'sonner';
import MemberLayout from '@/components/MemberLayout';
import { trackEvent, USAGE_EVENTS } from '@/utils/usageTracker';
import { 
  Wallet, 
  FileText, 
  Bell, 
  CreditCard, 
  Users, 
  ArrowRight,
  AlertCircle,
  CheckCircle,
  Clock,
  User
} from 'lucide-react';

const MemberDashboardOverview = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [balance, setBalance] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackEvent(USAGE_EVENTS.VIEW_DASHBOARD);
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileRes, balanceRes, invoicesRes, notifsRes] = await Promise.all([
        apiClient.get('/profile/me'),
        apiClient.get('/members/balance'),
        apiClient.get('/members/invoices'),
        apiClient.get('/notifications/my')
      ]);

      setProfile(profileRes.data);
      setBalance(balanceRes.data);
      setInvoices(invoicesRes.data);
      setNotifications(notifsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const currency = balance?.currency || 'USD';
  const outstandingBalance = balance?.outstanding_balance || 0;
  const unpaidInvoices = invoices.filter(inv => inv.status === 'unpaid');
  const unreadNotifications = notifications.filter(n => !n.is_read);

  if (loading) {
    return (
      <MemberLayout currentPage="dashboard" title="Dashboard" notificationCount={0}>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </MemberLayout>
    );
  }

  return (
    <MemberLayout currentPage="dashboard" title="Dashboard" notificationCount={unreadNotifications.length}>
      {/* Welcome Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-2">
          Welcome back, {profile?.first_name || user?.name || 'Member'}!
        </h2>
        <p className="text-muted-foreground">
          Here is an overview of your membership account.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className={outstandingBalance > 0 ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${outstandingBalance > 0 ? 'bg-amber-100' : 'bg-green-100'}`}>
                <Wallet className={`w-6 h-6 ${outstandingBalance > 0 ? 'text-amber-600' : 'text-green-600'}`} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Outstanding Balance</p>
                <p className={`text-xl font-bold ${outstandingBalance > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                  {formatCurrency(outstandingBalance, currency)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Payment Progress</p>
                <p className="text-xl font-bold text-purple-700">
                  {balance?.total_annual_fee > 0 
                    ? Math.round((balance?.total_paid / balance?.total_annual_fee) * 100)
                    : 0}%
                </p>
                <div className="w-full bg-purple-200 rounded-full h-1.5 mt-1">
                  <div 
                    className="bg-purple-600 h-1.5 rounded-full transition-all duration-300" 
                    style={{ width: `${Math.min(100, balance?.total_annual_fee > 0 ? (balance?.total_paid / balance?.total_annual_fee) * 100 : 0)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Link to="/dashboard/notifications" className="block">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100">
                  <Bell className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Notifications</p>
                  <p className="text-xl font-bold">{unreadNotifications.length}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </Link>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Member Status</p>
                <Badge variant="outline" className="mt-1 bg-emerald-50 text-emerald-700 border-emerald-200">
                  {profile?.status?.charAt(0).toUpperCase() + profile?.status?.slice(1) || 'Active'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Outstanding Invoices Alert */}
      {unpaidInvoices.length > 0 && (
        <Card className="mb-8 border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <AlertCircle className="w-5 h-5" />
              Outstanding Invoices
            </CardTitle>
            <CardDescription className="text-amber-600">
              You have {unpaidInvoices.length} unpaid invoice(s) totaling {formatCurrency(outstandingBalance, currency)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {unpaidInvoices.slice(0, 3).map((invoice) => (
                <div key={invoice.invoice_id} className="flex justify-between items-center p-3 bg-white rounded-lg border">
                  <div>
                    <p className="font-medium">{invoice.invoice_number}</p>
                    <p className="text-sm text-muted-foreground">{invoice.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{formatCurrency(invoice.amount, currency)}</p>
                    <p className="text-xs text-muted-foreground">
                      Due: {new Date(invoice.due_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/dashboard/payments">
              <Button className="w-full mt-4" data-testid="view-payments-btn">
                <CreditCard className="w-4 h-4 mr-2" />
                View All & Make Payment
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <Link to="/dashboard/payments">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <CreditCard className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Payments</h3>
                <p className="text-sm text-muted-foreground">View invoices & receipts</p>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground ml-auto" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/dashboard/documents">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Documents</h3>
                <p className="text-sm text-muted-foreground">Download organization files</p>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground ml-auto" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/dashboard/profile">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">My Profile</h3>
                <p className="text-sm text-muted-foreground">View & edit your details</p>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground ml-auto" />
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Recent Notifications */}
      {notifications.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent Notifications</CardTitle>
              <CardDescription>Your latest updates</CardDescription>
            </div>
            <Link to="/dashboard/notifications">
              <Button variant="outline" size="sm">View All</Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {notifications.slice(0, 3).map((notif) => (
                <div 
                  key={notif.notification_id} 
                  className={`p-3 rounded-lg border ${!notif.is_read ? 'bg-primary/5 border-primary/20' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-1 rounded ${!notif.is_read ? 'bg-primary/10' : 'bg-muted'}`}>
                      <Bell className={`w-4 h-4 ${!notif.is_read ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{notif.title}</p>
                      <p className="text-sm text-muted-foreground line-clamp-1">{notif.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(notif.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profile Summary */}
      <Card className="mt-8">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Profile Summary</CardTitle>
            <CardDescription>Your membership information</CardDescription>
          </div>
          <Link to="/dashboard/profile/edit">
            <Button variant="outline" size="sm">Edit Profile</Button>
          </Link>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Full Name</p>
              <p className="font-medium">{profile?.title} {profile?.first_name} {profile?.last_name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{profile?.email}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Contact</p>
              <p className="font-medium">{profile?.contact_number || 'Not set'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Member Since</p>
              <p className="font-medium">
                {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Work Station</p>
              <p className="font-medium">{profile?.work_station || 'Not set'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Specialty</p>
              <p className="font-medium">{profile?.specialty || 'Not set'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </MemberLayout>
  );
};

export default MemberDashboardOverview;
